import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Task } from "@lit/task";
import { fetchApi, types } from "../api/index.js";
import { apiLink } from "../utils/format.js";
import { displayCanonicalWithTags } from "../utils/canonical.js";
import "../components/loading-spinner.js";
import "../components/error-message.js";

type CategoryFilter = "all" | "build" | "runtime";

@customElement("sbom-tab")
export class SbomTab extends LitElement {
  @property() imageName = "";
  @property() canonical = "";
  @property({ type: Array }) canonicals: string[] = [];
  @property({ type: Array }) tags: types.Tag[] = [];
  @state() private _selectedArch = "";
  @state() private _filter = "";
  @state() private _category: CategoryFilter = "all";

  createRenderRoot() {
    return this;
  }

  private _task = new Task(this, {
    args: () => [this.imageName, this.canonical] as const,
    task: ([name, canonical]) =>
      canonical ? fetchApi<types.SbomResponse>(`/v1/images/${name}/sbom/${canonical}`) : Promise.reject("No canonical"),
  });

  private _activeArch(data: types.SbomResponse): string {
    const archs = Object.keys(data);
    if (this._selectedArch && archs.includes(this._selectedArch)) {
      return this._selectedArch;
    }
    return archs[0] ?? "amd64";
  }

  private _archData(data: types.SbomResponse): types.ArchSbom | undefined {
    return data[this._activeArch(data)];
  }

  private _filtered(pkgs: types.SbomPackage[]): types.SbomPackage[] {
    let filtered = pkgs.filter((p) => p.category !== "source");
    if (this._category !== "all") {
      filtered = filtered.filter((p) => p.category === this._category);
    }
    if (this._filter) {
      const q = this._filter.toLowerCase();
      filtered = filtered.filter((p) => p.name.toLowerCase().includes(q));
    }
    return filtered;
  }

  private _onCanonicalChange(e: Event) {
    this.dispatchEvent(new CustomEvent("canonical-change", { detail: (e.target as HTMLSelectElement).value }));
  }

  private _onArchChange(e: Event) {
    this._selectedArch = (e.target as HTMLSelectElement).value;
  }

  private _sourceBadge(pkg: types.SbomPackage) {
    if (pkg.category === "runtime") {
      return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold
        bg-teal-900/50 text-teal-400 border border-teal-700/50">RUNTIME</span>`;
    }
    return html`<span class="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold
      bg-blue-900/50 text-blue-400 border border-blue-700/50">BUILD</span>`;
  }

  private _typeBadge(pkg: types.SbomPackage) {
    const colors: Record<string, string> = {
      rpm: "text-gray-400",
      golang: "text-cyan-400",
      npm: "text-red-400",
      pypi: "text-yellow-400",
    };
    const cls = colors[pkg.type] ?? "text-gray-500";
    return html`<span class="text-[10px] font-mono ${cls}">${pkg.type}</span>`;
  }

  private _rowTint(pkg: types.SbomPackage): string {
    return pkg.category === "runtime" ? "bg-teal-950/10" : "bg-blue-950/10";
  }

  render() {
    return html`
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <div>
          <label class="text-gray-500 text-sm mr-1">Tag:</label>
          <select
            .value=${this.canonical}
            @change=${this._onCanonicalChange}
            class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
          >
            ${this.canonicals.map((c) => html`<option value=${c} ?selected=${c === this.canonical}>${displayCanonicalWithTags(c, this.tags)}</option>`)}
          </select>
        </div>
        ${this._renderArchSelect()}
        <div class="ml-auto">
          ${this.canonical ? apiLink(`/v1/images/${this.imageName}/sbom/${this.canonical}`) : ""}
        </div>
      </div>

      ${this._task.render({
        pending: () => html`<loading-spinner></loading-spinner>`,
        error: (e) => html`<error-message .message=${String(e)}></error-message>`,
        complete: (data) => this._renderData(data),
      })}
    `;
  }

  private _renderArchSelect() {
    const data = this._task.value;
    if (!data || Object.keys(data).length <= 1) return "";
    const active = this._activeArch(data);
    return html`
      <div>
        <label class="text-gray-500 text-sm mr-1">Arch:</label>
        <select
          .value=${active}
          @change=${this._onArchChange}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-sm text-gray-200"
        >
          ${Object.keys(data).map((a) => html`<option value=${a} ?selected=${a === active}>${a}</option>`)}
        </select>
      </div>
    `;
  }

  private _renderToggle(archData: types.ArchSbom) {
    const rc = archData.runtime_count ?? 0;
    const bc = archData.build_count ?? 0;
    const cats: { key: CategoryFilter; label: string; count: number }[] = [
      { key: "all", label: "All", count: rc + bc },
      { key: "build", label: "Build", count: bc },
      { key: "runtime", label: "Runtime", count: rc },
    ];
    return html`
      <div class="flex flex-wrap">
        ${cats.map(({ key, label, count }) => {
          const active = this._category === key;
          return html`<button
            @click=${() => (this._category = key)}
            class="px-2 py-1 text-xs cursor-pointer transition-colors border -ml-px first:ml-0
                   rounded-none first:rounded-l last:rounded-r
                   ${active
                     ? "bg-hb-accent text-hb-bg border-hb-accent relative z-10"
                     : "bg-hb-surface text-gray-400 hover:text-gray-200 border-hb-border"}"
          >${label} (${count})</button>`;
        })}
      </div>
    `;
  }

  private _renderData(data: types.SbomResponse) {
    const archData = this._archData(data);
    if (!archData) return html`<p class="text-gray-500 text-sm">No SBOM data available.</p>`;
    const filtered = this._filtered(archData.packages);
    const totalVisible = (archData.runtime_count ?? 0) + (archData.build_count ?? 0);
    return html`
      <div class="flex items-center justify-between mb-3">
        ${this._renderToggle(archData)}
        <input
          type="text"
          placeholder="Filter packages..."
          .value=${this._filter}
          @input=${(e: Event) => (this._filter = (e.target as HTMLInputElement).value)}
          class="bg-hb-surface border border-hb-border rounded px-2 py-1 text-xs text-gray-200
                 placeholder:text-gray-500 focus:outline-none focus:border-hb-accent w-40"
        />
      </div>

      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3 w-24">Source</th>
              <th class="py-2 px-3 w-16">Type</th>
              <th class="py-2 px-3">Name</th>
              <th class="py-2 px-3">Version</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(
              (pkg) => html`
                <tr class="${this._rowTint(pkg)} border-b border-hb-border/50 hover:bg-hb-surface/30">
                  <td class="py-1.5 px-3">${this._sourceBadge(pkg)}</td>
                  <td class="py-1.5 px-3">${this._typeBadge(pkg)}</td>
                  <td class="py-1.5 px-3 text-gray-200 font-mono">${pkg.name}</td>
                  <td class="py-1.5 px-3 text-gray-400 font-mono">${pkg.evr}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
      ${this._filter
        ? html`<p class="text-gray-500 text-xs mt-2">Showing ${filtered.length} of ${totalVisible} packages</p>`
        : ""}
    `;
  }
}
